import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import { useNavigate } from 'react-router-dom'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { usePagination, useDialog, useLoading } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { exportToCSV } from '../../utils/exportUtils'
import { attendanceApi } from '../../api/attendanceApi'
import { dashboardApi } from '../../api/dashboardApi'
import { format } from 'date-fns'
import { formatDate } from '../../utils/formatters'
import toast from 'react-hot-toast'

const Attendance = () => {
  const navigate = useNavigate()
  const {
    isOpen: viewDetailsOpen,
    data: selectedRecord,
    openDialog: openViewDetails,
    closeDialog: closeViewDetails
  } = useDialog()
  const pagination = usePagination(10)
  const { loading, withLoading } = useLoading()
  const [attendanceData, setAttendanceData] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [summary, setSummary] = useState({
    present: 0,
    absent: 0,
    avgCheckIn: '--:--'
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDate, setSelectedDate] = useState(null)
  const [filterDepartment, setFilterDepartment] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [attendanceTrend, setAttendanceTrend] = useState([])

  const fetchAttendance = async () => {
    withLoading(async () => {
      try {
        const params = {
          page: pagination.page + 1, // API is 1-indexed
          limit: pagination.rowsPerPage,
        }

        if (searchTerm) params.search = searchTerm
        if (selectedDate) params.date = format(selectedDate, 'yyyy-MM-dd')
        if (filterDepartment && filterDepartment !== 'all') params.department = filterDepartment
        if (filterStatus && filterStatus !== 'all') params.status = filterStatus

        const response = await attendanceApi.getOrgAttendance(params)

        // Handle different response structures gracefully
        if (response.data && Array.isArray(response.data.data)) {
          setAttendanceData(response.data.data)
          setTotalCount(response.data.total_records || response.data.total || 0)
          setSummary({
            present: response.data.present || 0,
            absent: response.data.absent || 0,
            avgCheckIn: response.data.avg_checkin || '--:--'
          })
        } else {
          setAttendanceData([])
          setTotalCount(0)
          setSummary({ present: 0, absent: 0, avgCheckIn: '--:--' })
        }
      } catch (error) {
        console.error('Failed to fetch attendance:', error)
        toast.error('Failed to load attendance records')
        setAttendanceData([])
      }
    })
  }

  const fetchAttendanceTrend = async () => {
    try {
      const response = await dashboardApi.getHourlyCheckins()
      if (response.data?.hourly_checkins) {
        const hourlyData = response.data.hourly_checkins
        const hours = ['9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM']
        const formattedData = hours.map(time => ({
          time,
          count: hourlyData[time] || 0
        }))
        setAttendanceTrend(formattedData)
      }
    } catch (error) {
      console.error('Error fetching hourly trends:', error)
    }
  }

  useEffect(() => {
    fetchAttendanceTrend()
  }, [])

  useEffect(() => {
    fetchAttendance()
  }, [pagination.page, pagination.rowsPerPage, searchTerm, selectedDate, filterDepartment, filterStatus])


  const handleExport = () => {
    const columns = [
      { label: 'Employee Name', key: 'name' },
      { label: 'Employee Code', key: 'employee_code' },
      { label: 'Department', key: 'department' },
      { label: 'Date', key: 'date' },
      { label: 'Check In', key: 'check_in' },
      { label: 'Check Out', key: 'check_out' },
      { label: 'Working Hours', key: 'working_hours' },
      { label: 'Status', key: 'status' },
      { label: 'Device', key: 'device' }
    ]
    exportToCSV(attendanceData, columns, 'attendance-records')
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* View Details Dialog */}
      <Dialog open={viewDetailsOpen} onClose={closeViewDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Attendance Details</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#1976d2' }}>
                      {selectedRecord.name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography fontWeight="medium">{selectedRecord.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {selectedRecord.employee_code} • {selectedRecord.department}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Date</Typography>
                  <Typography>{formatDate(selectedRecord.date)}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <StatusChip status={selectedRecord.status} variant="outlined" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Check In</Typography>
                  <Typography>{selectedRecord.check_in || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Check Out</Typography>
                  <Typography>{selectedRecord.check_out || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Working Hours</Typography>
                  <Typography>{selectedRecord.working_hours || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Device</Typography>
                  <Typography>{selectedRecord.device || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">Overtime</Typography>
                  <Typography>{selectedRecord.overtime || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              Attendance Management
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalCount}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Records
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {summary.present}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Present
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#dc004e', mr: 2 }}>
                      <ErrorIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {summary.absent}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Absent
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#ed6c02', mr: 2 }}>
                      <AccessTimeIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {summary.avgCheckIn}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Avg. Check-in
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Select Date"
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search employees..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select
                    label="Department"
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    <MenuItem value="HR">HR</MenuItem>
                    <MenuItem value="IT">IT</MenuItem>
                    <MenuItem value="Sales">Sales</MenuItem>
                    <MenuItem value="Production">Production</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="late">Late</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                    <MenuItem value="half-day">Half Day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

            </Grid>
          </Paper>
        </Box>

        {/* Attendance Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, mt: 3, mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Attendance Records
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('')
                  setFilterDepartment('all')
                  setFilterStatus('all')
                  setSelectedDate(null)
                }}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Export
              </Button>
              <Button
                variant="contained"
                startIcon={<CalendarIcon />}
                onClick={() => navigate('/org/attendance/summary')}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Reports
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Check In</TableCell>
                  <TableCell>Check Out</TableCell>
                  <TableCell>Working Hours</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={8} rows={pagination.rowsPerPage} />
                ) : attendanceData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No attendance records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  attendanceData.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                            {record.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {record.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {record.employee_code} • {record.department}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(record.date)}</TableCell>
                      <TableCell>{record.check_in || '--:--'}</TableCell>
                      <TableCell>{record.check_out || '--:--'}</TableCell>
                      <TableCell>{record.working_hours || '-'}</TableCell>
                      <TableCell>
                        <StatusChip status={record.status} variant="outlined" />
                      </TableCell>
                      <TableCell>{record.device || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openViewDetails(record)}
                          title="View Details"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>

        {/* Chart */}
        <Grid container spacing={3}>
          {/* Chart */}
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Today's Check-ins
              </Typography>
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={attendanceTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1976d2" name="Check-ins" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box >
  )
}

export default Attendance
